import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import Badge from "@mui/material/Badge";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import Popover from "@mui/material/Popover";
import Snackbar from "@mui/material/Snackbar";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { api } from "../lib/api";
import { describeCheckError } from "../lib/check-error";
import { getCountry, COUNTRY_META } from "../lib/country";
import { onCheckFailed, onDomainExpiring } from "../lib/socket";
import { useI18n, type Translate } from "../lib/i18n";
import type { DomainExpiringEvent, LinkCheckFailedEvent, Notification } from "../lib/types";

interface NotificationsValue {
  items: Notification[];
  unread: number;
  connected: boolean;
  markRead: (id: number) => void;
  markAllRead: () => void;
  reload: () => void;
}

const NotificationsContext = createContext<NotificationsValue | null>(null);

const MAX_ITEMS = 100;

/** Chuẩn hoá payload realtime thành Notification hoàn chỉnh (bù createdAt nếu thiếu). */
function toNotification(ev: LinkCheckFailedEvent): Notification {
  return {
    ...ev,
    read: ev.read ?? false,
    createdAt: ev.createdAt ?? ev.checkedAt ?? new Date().toISOString(),
  };
}

/** Chuẩn hoá sự kiện domain sắp hết hạn thành Notification. */
function domainExpiringToNotification(ev: DomainExpiringEvent): Notification {
  return {
    id: ev.id ?? Date.now(),
    type: "domain-expiring",
    linkId: ev.linkId,
    country: ev.country,
    brand: ev.brand,
    domain: ev.domain,
    source: null,
    deviceId: null,
    deviceName: null,
    healthStatus: null,
    statusCode: null,
    error: null,
    errorCode: null,
    errorDetail: null,
    checkerIp: null,
    network: null,
    region: null,
    checkedAt: null,
    expiryAt: ev.expiryAt,
    daysToExpiry: ev.daysToExpiry,
    registrar: ev.registrar,
    read: ev.read ?? false,
    createdAt: new Date().toISOString(),
  };
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Notification[]>([]);
  const [unread, setUnread] = useState(0);
  const [connected, setConnected] = useState(false);
  const [toast, setToast] = useState<Notification | null>(null);
  // Giữ id đã thấy để realtime không tạo bản trùng với dữ liệu vừa fetch.
  const seen = useRef<Set<number>>(new Set());

  const reload = useCallback(async () => {
    try {
      const activeCountry = getCountry();
      const [list, count] = await Promise.all([
        api.notifications.list({ limit: 50 }),
        api.notifications.unreadCount(),
      ]);
      // Chỉ giữ thông báo khớp quốc gia đang xem
      const filtered = list.filter(n => !n.country || n.country === activeCountry);
      seen.current = new Set(filtered.map(n => n.id));
      setItems(filtered);
      setUnread(count.unread);
    } catch {
      // Lỗi tải lịch sử không nên chặn app — realtime vẫn hoạt động độc lập.
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    const unsubFailed = onCheckFailed(ev => {
      const activeCountry = getCountry();
      // Bỏ qua thông báo khác quốc gia đang xem
      if (ev.country && ev.country !== activeCountry) return;

      const n = toNotification(ev);
      setConnected(true);
      if (n.id != null && seen.current.has(n.id)) return;
      if (n.id != null) seen.current.add(n.id);
      setItems(prev => [n, ...prev].slice(0, MAX_ITEMS));
      if (!n.read) setUnread(u => u + 1);
      setToast(n);
    });

    const unsubExpiring = onDomainExpiring(ev => {
      const activeCountry = getCountry();
      // Bỏ qua thông báo khác quốc gia đang xem
      if (ev.country && ev.country !== activeCountry) return;

      const n = domainExpiringToNotification(ev);
      setConnected(true);
      if (n.id != null && seen.current.has(n.id)) return;
      if (n.id != null) seen.current.add(n.id);
      setItems(prev => [n, ...prev].slice(0, MAX_ITEMS));
      if (!n.read) setUnread(u => u + 1);
      setToast(n);
    });

    return () => {
      unsubFailed();
      unsubExpiring();
    };
  }, []);

  const markRead = useCallback((id: number) => {
    setItems(prev => {
      const target = prev.find(n => n.id === id);
      if (!target || target.read) return prev;
      setUnread(u => Math.max(0, u - 1));
      return prev.map(n => (n.id === id ? { ...n, read: true } : n));
    });
    // Bỏ qua lỗi mạng: trạng thái đã đọc không quan trọng bằng trải nghiệm mượt.
    api.notifications.read(id).catch(() => {});
  }, []);

  const markAllRead = useCallback(() => {
    setItems(prev => prev.map(n => ({ ...n, read: true })));
    setUnread(0);
    api.notifications.readAll().catch(() => {});
  }, []);

  const value = useMemo<NotificationsValue>(
    () => ({ items, unread, connected, markRead, markAllRead, reload }),
    [items, unread, connected, markRead, markAllRead, reload],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
      <NotificationToast notification={toast} onClose={() => setToast(null)} />
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) throw new Error("useNotifications phải nằm trong <NotificationsProvider>.");
  return ctx;
}

/** Toast góc màn hình mỗi khi có link check lỗi mới hoặc domain sắp hết hạn. */
function NotificationToast({
  notification,
  onClose,
}: {
  notification: Notification | null;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const isExpiring = notification?.type === "domain-expiring";
  const isExpired = isExpiring && (notification?.daysToExpiry ?? 0) < 0;
  const title = isExpiring
    ? isExpired
      ? t("table.expiry")
      : t("links.expiryLabel")
    : t("notif.toastTitle");

  return (
    <Snackbar
      open={!!notification}
      autoHideDuration={6000}
      onClose={(_e, reason) => reason !== "clickaway" && onClose()}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    >
      <Alert
        severity={isExpired ? "error" : "warning"}
        variant="filled"
        onClose={onClose}
        sx={{ maxWidth: 360 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
        {notification && (
          <Typography variant="caption" sx={{ display: "block", opacity: 0.95 }}>
            {notifSummary(notification, t)}
          </Typography>
        )}
      </Alert>
    </Snackbar>
  );
}

function notifSummary(n: Notification, t: Translate): string {
  const link = [n.brand, n.domain].filter(Boolean).join(" · ") || `#${n.linkId ?? "?"}`;
  if (n.type === "domain-expiring") {
    const d = n.daysToExpiry;
    if (d == null) return `${link} — ${t("links.expiryLabel")}`;
    if (d < 0) {
      return `${link} — ${t("table.expiry")} (${Math.abs(d)}d)`;
    }
    return `${link} — ${t("links.expiryLabel")} (${d}d)`;
  }
  // IP đã check đi cùng nơi check — biết ngay IP nào đang bị chặn
  const where = [n.deviceName, n.region, n.checkerIp].filter(Boolean).join(" · ");
  const err = describeCheckError(
    t,
    { code: n.errorCode, statusCode: n.statusCode, detail: n.errorDetail },
    n.error,
  );
  const code = n.statusCode ? ` (${n.statusCode})` : err ? ` (${err})` : "";
  return where ? `${link}${code} — ${where}` : `${link}${code}`;
}

/** Câu lỗi theo ngôn ngữ đang chọn; `error` thô chỉ là dự phòng cho bản ghi cũ. */
function errorText(n: Notification, t: Translate): string | null {
  return describeCheckError(
    t,
    { code: n.errorCode, statusCode: n.statusCode, detail: n.errorDetail },
    n.error,
  );
}

/** Chuông 🔔 + badge số chưa đọc, mở popover danh sách thông báo. */
export function NotificationBell({ navigate }: { navigate?: (to: string) => void }) {
  const { t, fmtAgo, fmtDate } = useI18n();
  const { items, unread, markRead, markAllRead } = useNotifications();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const currentCountry = getCountry();
  const countryMeta = COUNTRY_META[currentCountry];

  function openItem(n: Notification) {
    markRead(n.id);
    if (navigate) {
      if (n.type === "domain-expiring" && n.domain) {
        navigate(`links?search=${encodeURIComponent(n.domain)}`);
      } else if (n.linkId != null) {
        navigate(`checks?linkId=${n.linkId}`);
      }
      setAnchor(null);
    }
  }

  return (
    <>
      <Tooltip title={t("notif.title")}>
        <IconButton onClick={e => setAnchor(e.currentTarget)} aria-label={t("notif.title")}>
          <Badge badgeContent={unread} color="error" max={99}>
            <NotificationsNoneIcon />
          </Badge>
        </IconButton>
      </Tooltip>

      <Popover
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { width: 380, maxWidth: "92vw" } } }}
      >
        <Stack
          direction="row"
          sx={{ alignItems: "center", justifyContent: "space-between", px: 2, py: 1.25 }}
        >
          <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
            <Typography sx={{ fontWeight: 700 }}>{t("notif.title")}</Typography>
            <Chip
              size="small"
              variant="outlined"
              label={`${countryMeta?.flag ?? ""} ${countryMeta?.label ?? currentCountry.toUpperCase()}`}
              sx={{ height: 22, fontSize: 11, fontWeight: 600 }}
            />
          </Stack>
          <Button
            size="small"
            startIcon={<DoneAllIcon fontSize="small" />}
            onClick={markAllRead}
            disabled={unread === 0}
          >
            {t("notif.markAllRead")}
          </Button>
        </Stack>
        <Divider />

        {items.length === 0 ? (
          <Box sx={{ px: 2, py: 4, textAlign: "center", color: "text.secondary", fontSize: 13 }}>
            {t("notif.empty")}
          </Box>
        ) : (
          <List sx={{ py: 0, maxHeight: 440, overflowY: "auto" }}>
            {items.map(n => (
              <ListItemButton
                key={n.id}
                onClick={() => openItem(n)}
                sx={{
                  alignItems: "flex-start",
                  gap: 1,
                  py: 1.25,
                  borderLeft: 3,
                  borderColor: n.read ? "transparent" : "warning.main",
                  bgcolor: n.read ? "transparent" : "action.hover",
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="body2" noWrap sx={{ fontWeight: n.read ? 500 : 700 }}>
                    {[n.brand, n.domain].filter(Boolean).join(" · ") || `#${n.linkId ?? "?"}`}
                  </Typography>
                  {n.type === "domain-expiring" ? (
                    <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", mt: 0.5 }} useFlexGap>
                      <Chip
                        size="small"
                        color={n.daysToExpiry != null && n.daysToExpiry < 0 ? "error" : "warning"}
                        label={
                          n.daysToExpiry != null && n.daysToExpiry < 0
                            ? `${t("table.expiry")} (${Math.abs(n.daysToExpiry)}d)`
                            : `${t("links.expiryLabel")} (${n.daysToExpiry ?? "?"}d)`
                        }
                      />
                      {n.registrar && <Chip size="small" variant="outlined" label={n.registrar} />}
                    </Stack>
                  ) : (
                    <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", mt: 0.5 }} useFlexGap>
                      <Chip
                        size="small"
                        color={n.healthStatus === "unhealthy" ? "error" : "default"}
                        label={t(`status.${n.healthStatus ?? "unknown"}`)}
                      />
                      <Chip
                        size="small"
                        variant="outlined"
                        color={n.source === "server" ? "primary" : "default"}
                        label={
                          n.source === "server"
                            ? t("checks.sourceServer")
                            : n.deviceName ?? t("checks.sourceDevice")
                        }
                      />
                      {n.region && <Chip size="small" variant="outlined" label={n.region} />}
                      {n.statusCode != null && (
                        <Chip size="small" variant="outlined" label={`HTTP ${n.statusCode}`} />
                      )}
                      {n.checkerIp && (
                        <Tooltip title={t("table.checkerIpHint")}>
                          <Chip size="small" variant="outlined" label={n.checkerIp} />
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                  {errorText(n, t) && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: "block", mt: 0.5 }}>
                      {errorText(n, t)}
                    </Typography>
                  )}
                </Box>
                <Tooltip title={fmtDate(n.checkedAt ?? n.createdAt)}>
                  <Typography variant="caption" color="text.secondary" sx={{ flex: "none", whiteSpace: "nowrap" }}>
                    {fmtAgo(n.checkedAt ?? n.createdAt)}
                  </Typography>
                </Tooltip>
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
}
